import { apiService } from './api';
import { cacheService } from './cacheService';
import { logActivity } from '../utils/activityLogger';
import { offlineQueue } from './offlineQueue';
import { locationService } from './locationService';
import { getDeviceInfo } from './deviceInfo';

const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

interface User {
  id: string;
  // Add other user properties as needed
}

export class SyncService {
  private isSyncing: boolean = false;
  private lastSyncTime: number = 0;
  private syncIntervalId: NodeJS.Timeout | null = null;
  private syncListeners: Array<() => void> = [];

  constructor() {
    this.initialize();
  }

  private initialize() {
    if (typeof window === 'undefined') return;
    
    // Set up online/offline event listeners
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Start periodic sync
    this.startPeriodicSync();
    
    // Initial sync check
    this.syncIfNeeded().catch(console.error);
  }

  private handleOnline = async () => {
    console.log('Device is online, starting sync...');
    await this.syncIfNeeded();
  };

  private handleOffline = () => {
    console.log('Device is offline, sync paused');
    this.isSyncing = false;
  };

  public startPeriodicSync() {
    if (this.syncIntervalId) return;
    
    this.syncIntervalId = setInterval(() => {
      if (navigator.onLine) {
        this.syncIfNeeded().catch(console.error);
      }
    }, SYNC_INTERVAL);
  }

  public stopPeriodicSync() {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
  }

  public subscribe(listener: () => void): () => void {
    this.syncListeners.push(listener);
    return () => {
      this.syncListeners = this.syncListeners.filter(l => l !== listener);
    };
  }

  private notifySyncListeners() {
    this.syncListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  private async getCurrentUser(): Promise<User | null> {
    try {
      // Replace with your actual user fetching logic
      return await apiService.getCurrentUser();
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  public async syncIfNeeded(force: boolean = false): Promise<void> {
    if (this.isSyncing && !force) {
      console.log('Sync already in progress, skipping...');
      return;
    }

    const now = Date.now();
    if (!force && now - this.lastSyncTime < SYNC_INTERVAL) {
      console.log('Last sync was too recent, skipping...');
      return;
    }

    try {
      this.isSyncing = true;
      this.notifySyncListeners();
      
      // Get current location for sync
      const location = await locationService.getCurrentLocation();
      const deviceInfo = await getDeviceInfo();
      
      // Process offline queue first
      await offlineQueue.processQueue();
      
      // Get user info
      const user = await this.getCurrentUser();
      
      if (!user) {
        console.log('No user found, skipping sync');
        return;
      }

      console.log('Starting data sync...');
      
      // Log sync start
      await logActivity(
        'INFO',
        `Sync started for user ${user.id}`,
        {
          type: 'SYNC_START',
          userId: user.id,
          deviceId: deviceInfo.deviceId,
          location: location || undefined,
          timestamp: new Date().toISOString()
        }
      );
      
// Sync projects
      await this.syncProjects();
      
      // Update last sync time
      this.lastSyncTime = Date.now();
      localStorage.setItem('lastSyncTime', this.lastSyncTime.toString());
      
      // Log sync end
      await logActivity(
        'INFO',
        `Sync completed for user ${user.id}`,
        {
          type: 'SYNC_END',
          userId: user.id,
          deviceId: deviceInfo.deviceId,
          location: location || undefined,
          timestamp: new Date().toISOString()
        }
      );
      
      console.log('Sync completed successfully');
    } catch (error) {
      console.error('Error during sync:', error);
      
      // Log sync error
      const user = await this.getCurrentUser();
      if (user) {
        const deviceInfo = await getDeviceInfo();
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        await logActivity(
          'ERROR',
          `Sync failed for user ${user.id}: ${errorMessage}`,
          {
            type: 'SYNC_ERROR',
            userId: user.id,
            deviceId: deviceInfo.deviceId,
            error: errorMessage,
            timestamp: new Date().toISOString()
          }
        );
      }
    } finally {
      this.isSyncing = false;
      this.notifySyncListeners();
    }
  }

  private async syncProjects(): Promise<void> {
    try {
      console.log('Syncing projects...');
      const projects = await apiService.getProjects(true);
      // Cache projects
      await cacheService.cacheProjects(projects);
      console.log(`Synced ${projects.length} projects`);
    } catch (error) {
      console.error('Error syncing projects:', error);
      throw error;
    }
  }

  // Public method to force sync
  async forceSync(): Promise<void> {
    console.log('Forcing sync...');
    await this.syncIfNeeded(true);
  }

  // Cleanup
  public destroy() {
    this.stopPeriodicSync();
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }
}

export const syncService = new SyncService();

// Start sync service when the app loads
if (typeof window !== 'undefined') {
  syncService.startPeriodicSync();
}